import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBell,
  FaCalendarCheck,
  FaChartLine,
  FaCheckCircle,
  FaCoins,
  FaDownload,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaFire,
  FaPrint,
  FaQrcode,
  FaShoppingBag,
  FaStar,
  FaStore,
  FaSync,
  FaTicketAlt,
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

type Props = {
  business: any;
  stats: { views?: number; clicks?: number; revenue?: number };
  orders: any[];
  reservations: any[];
  shopVouchers: any[];
  deals: any[];
  products: any[];
  boutiqueForfaits: any[];
  boutiqueReviews: any[];
  locale?: string;
  onNavigate?: (tab: string) => void;
};

type RemotePerformance = {
  ok?: boolean;
  generated_at?: string;
  summary?: Record<string, any>;
  reviews?: any[];
  recent_orders?: any[];
  recent_vouchers?: any[];
  recent_reservations?: any[];
  recommendations?: string[];
  warnings?: string[];
};

const money = (value: any) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue)
    ? numberValue.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })
    : '0,00 $';
};

const number = (value: any) => Number(value || 0).toLocaleString('fr-CA');

const safeDate = (value: any) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const csvEscape = (value: any) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const buildCsv = (rows: any[]) => {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  return [headers.join(','), ...rows.map((row) => headers.map((h) => csvEscape(row[h])).join(','))].join('\n');
};

const downloadTextFile = (filename: string, content: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const metricTone = {
  blue: 'from-blue-600 to-cyan-500 shadow-blue-500/20',
  green: 'from-emerald-600 to-teal-500 shadow-emerald-500/20',
  amber: 'from-amber-500 to-orange-500 shadow-amber-500/20',
  violet: 'from-violet-600 to-fuchsia-500 shadow-violet-500/20',
  rose: 'from-rose-600 to-pink-500 shadow-rose-500/20',
  slate: 'from-white to-slate-600 shadow-slate-500/20',
};

const MetricCard = ({ icon, label, value, sub, tone = 'blue' }: any) => (
  <div className="relative overflow-hidden rounded-[2rem] bg-white border border-slate-100 p-5 shadow-sm hover:shadow-xl transition-all">
    <div className={`absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br ${metricTone[tone as keyof typeof metricTone]} opacity-10`} />
    <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${metricTone[tone as keyof typeof metricTone]} text-white shadow-lg`}>
      {icon}
    </div>
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-700">{label}</p>
    <p className="mt-2 text-2xl md:text-3xl font-black text-slate-900">{value}</p>
    {sub && <p className="mt-2 text-xs font-bold text-slate-700">{sub}</p>}
  </div>
);

const QualityPill = ({ type, children }: any) => {
  const classes =
    type === 'warning'
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : type === 'danger'
      ? 'bg-red-50 text-red-800 border-red-200'
      : 'bg-emerald-50 text-emerald-800 border-emerald-200';
  return <div className={`rounded-2xl border px-4 py-3 text-sm font-bold ${classes}`}>{children}</div>;
};

export default function MerchantPerformancePanel({
  business,
  stats,
  orders,
  reservations,
  shopVouchers,
  deals,
  products,
  boutiqueForfaits,
  boutiqueReviews,
  locale = 'fr-CA',
  onNavigate,
}: Props) {
  const [remote, setRemote] = useState<RemotePerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [remoteError, setRemoteError] = useState<string | null>(null);

  const paidOrders = useMemo(
    () => orders.filter((o) => ['paid', 'picked_up', 'fulfilled'].includes(String(o.status || o.fulfillment_status || '').toLowerCase())),
    [orders]
  );

  const redeemedVouchers = useMemo(
    () => shopVouchers.filter((v) => ['redeemed', 'used'].includes(String(v.status || v.effective_status || '').toLowerCase())),
    [shopVouchers]
  );

  const activeVouchers = useMemo(
    () => shopVouchers.filter((v) => ['active', 'issued'].includes(String(v.status || v.effective_status || '').toLowerCase())),
    [shopVouchers]
  );

  const confirmedReservations = useMemo(
    () => reservations.filter((r) => ['confirmed', 'paid'].includes(String(r.status || r.payment_status || '').toLowerCase())),
    [reservations]
  );

  const localRevenue = useMemo(() => {
    const orderRevenue = paidOrders.reduce((sum, order) => sum + Number(order.total_amount || order.amount_total || 0), 0);
    const reservationRevenue = reservations
      .filter((r) => ['paid', 'confirmed'].includes(String(r.payment_status || r.status || '').toLowerCase()))
      .reduce((sum, r) => sum + Number(r.total_price || 0), 0);
    return orderRevenue + reservationRevenue;
  }, [paidOrders, reservations]);

  const reviewAverage = useMemo(() => {
    const ratings = boutiqueReviews.map((r) => Number(r.rating || r.note || 0)).filter((r) => r > 0);
    if (!ratings.length && remote?.summary?.average_rating) return Number(remote.summary.average_rating);
    if (!ratings.length) return 0;
    return ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
  }, [boutiqueReviews, remote]);

  const recommendations = useMemo(() => {
    const items: string[] = [];
    if ((stats.views || 0) > 0 && paidOrders.length === 0) items.push('Votre fiche reçoit des visites, mais peu de commandes : ajoutez une offre claire avec un prix simple et une photo forte.');
    if (activeVouchers.length > 0 && redeemedVouchers.length === 0) items.push('Des vouchers sont actifs : invitez les clients à présenter leur QR code à l’arrivée.');
    if (products.length > 0 && products.filter((p) => Number(p.stock_quantity || 0) <= 2).length > 0) items.push('Certains produits ont un stock faible : mettez les quantités à jour pour éviter les mauvaises surprises.');
    if (deals.length === 0) items.push('Activez une offre Last Call pour remplir les périodes creuses ou liquider les invendus.');
    if (boutiqueForfaits.length === 0) items.push('Ajoutez un forfait boutique prêt à offrir pour capter les achats cadeaux.');
    if (reviewAverage > 0 && reviewAverage < 4) items.push('Votre moyenne d’avis mérite une attention : répondez aux commentaires et corrigez les irritants récurrents.');
    return [...(remote?.recommendations || []), ...items].slice(0, 8);
  }, [stats.views, paidOrders.length, activeVouchers.length, redeemedVouchers.length, products, deals.length, boutiqueForfaits.length, reviewAverage, remote]);

  const warnings = useMemo(() => {
    const items: string[] = [];
    if (!business?.image_url) items.push('Votre fiche n’a pas encore de photo principale.');
    if (!business?.description || String(business.description).length < 80) items.push('Votre description publique est courte : ajoutez plus d’émotion, de détails et de mots-clés locaux.');
    if (!business?.phone && !business?.contact_email) items.push('Ajoutez un téléphone ou un courriel de contact pour rassurer les clients.');
    if (shopVouchers.filter((v) => String(v.status || '').toLowerCase() === 'expired').length > 0) items.push('Des vouchers expirés existent : vérifiez les demandes clients et les politiques internes.');
    return [...(remote?.warnings || []), ...items].slice(0, 8);
  }, [business, shopVouchers, remote]);

  const loadRemote = async () => {
    if (!business?.id) return;
    setLoading(true);
    setRemoteError(null);
    try {
      const { data, error } = await supabase.functions.invoke('merchant-dashboard-performance', {
        body: { business_id: business.id },
      });
      if (error) throw error;
      setRemote(data || null);
    } catch (error: any) {
      setRemoteError(error?.message || 'Analyse serveur indisponible. Les données locales restent affichées.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRemote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const exportCsv = () => {
    const rows = [
      ...paidOrders.map((order) => ({ type: 'commande', id: order.id, date: order.created_at, statut: order.status, total: order.total_amount || order.amount_total || 0, client: order.customer_email || order.email || '' })),
      ...redeemedVouchers.map((voucher) => ({ type: 'voucher_valide', id: voucher.id, date: voucher.redeemed_at || voucher.updated_at, statut: voucher.status, total: voucher.amount || voucher.initial_amount || '', client: voucher.customer_email || '' })),
      ...confirmedReservations.map((reservation) => ({ type: 'reservation', id: reservation.id, date: reservation.start_at || reservation.created_at, statut: reservation.status, total: reservation.total_price || '', client: reservation.guest_email || '' })),
    ];
    const csv = buildCsv(rows.length ? rows : [{ type: 'aucune_donnee', id: '', date: '', statut: '', total: '', client: '' }]);
    downloadTextFile(`forfeo-performance-${business?.id || 'commerce'}.csv`, csv);
  };

  const printReport = () => window.print();

  const revenue = remote?.summary?.estimated_revenue ?? stats.revenue ?? localRevenue;
  const averageRating = remote?.summary?.average_rating ?? reviewAverage;
  const reviewCount = remote?.summary?.review_count ?? boutiqueReviews.length;

  return (
    <div className="space-y-8 animate-in fade-in" data-forfeo-ui-v218="performance" data-commerce-panel-v219="performance" data-commerce-v220-panel="performance">
      <div className="relative overflow-hidden rounded-[2.5rem] bg-white p-6 md:p-10 text-slate-900 shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.35),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.25),transparent_35%)]" />
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-blue-50 px-4 py-2 text-[10px] font-black uppercase tracking-[0.25em] text-blue-700">
              <FaChartLine /> Centre de performance marchand
            </div>
            <h2 className="mt-5 text-3xl md:text-5xl font-black tracking-tight">Tableau de bord 2.0</h2>
            <p className="mt-4 max-w-3xl text-sm md:text-base font-medium text-slate-700">
              Une vue claire pour suivre les ventes, les validations, les réservations, les avis et les actions prioritaires de votre commerce sur Forfeo Explore.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={loadRemote} disabled={loading} className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-white px-5 py-3 text-xs font-black uppercase tracking-widest text-blue-700 hover:bg-blue-50 disabled:opacity-60">
              <FaSync className={loading ? 'animate-spin' : ''} /> Actualiser
            </button>
            <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-400">
              <FaDownload /> CSV
            </button>
            <button onClick={printReport} className="inline-flex items-center gap-2 rounded-2xl bg-blue-500 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-blue-400">
              <FaPrint /> Imprimer
            </button>
          </div>
        </div>
      </div>

      {remoteError && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800">
          {remoteError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <MetricCard icon={<FaCoins />} label="Revenus estimés" value={money(revenue)} sub="Commandes + réservations payées" tone="green" />
        <MetricCard icon={<FaShoppingBag />} label="Commandes" value={number(remote?.summary?.paid_orders ?? paidOrders.length)} sub={`${orders.length} au total`} tone="violet" />
        <MetricCard icon={<FaQrcode />} label="Vouchers validés" value={number(remote?.summary?.redeemed_vouchers ?? redeemedVouchers.length)} sub={`${activeVouchers.length} actifs`} tone="blue" />
        <MetricCard icon={<FaCalendarCheck />} label="Réservations" value={number(remote?.summary?.confirmed_reservations ?? confirmedReservations.length)} sub="Confirmées / payées" tone="amber" />
        <MetricCard icon={<FaStar />} label="Réputation" value={averageRating ? `${Number(averageRating).toFixed(1)}/5` : '—'} sub={`${number(reviewCount)} avis`} tone="rose" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h3 className="text-xl font-black text-slate-900">Activité récente</h3>
              <p className="text-sm font-medium text-slate-700">Commandes, vouchers et réservations à surveiller.</p>
            </div>
            {onNavigate && (
              <button onClick={() => onNavigate('scanner')} className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:bg-blue-700">
                Scanner
              </button>
            )}
          </div>

          <div className="space-y-3">
            {[...paidOrders.slice(0, 4).map((order) => ({ icon: <FaShoppingBag />, title: `Commande ${String(order.id || '').slice(0, 8).toUpperCase()}`, meta: `${safeDate(order.created_at)} · ${money(order.total_amount || order.amount_total || 0)}`, tone: 'bg-violet-50 text-violet-700' })),
              ...redeemedVouchers.slice(0, 4).map((voucher) => ({ icon: <FaQrcode />, title: `Voucher ${voucher.voucher_code || voucher.code || String(voucher.id || '').slice(0, 8)}`, meta: `${safeDate(voucher.redeemed_at || voucher.updated_at)} · ${voucher.customer_email || 'Client Forfeo'}`, tone: 'bg-blue-50 text-blue-700' })),
              ...confirmedReservations.slice(0, 4).map((reservation) => ({ icon: <FaCalendarCheck />, title: reservation.guest_name || 'Réservation Forfeo', meta: `${safeDate(reservation.start_at || reservation.created_at)} · ${money(reservation.total_price || 0)}`, tone: 'bg-amber-50 text-amber-700' })),
            ].slice(0, 9).map((item, index) => (
              <div key={`${item.title}-${index}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.tone}`}>{item.icon}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-black text-slate-900">{item.title}</p>
                  <p className="truncate text-xs font-bold text-slate-700">{item.meta}</p>
                </div>
              </div>
            ))}

            {paidOrders.length === 0 && redeemedVouchers.length === 0 && confirmedReservations.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-700">
                Aucune activité récente. Ajoutez une offre ou partagez votre fiche Forfeo pour déclencher les premières conversions.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
            <h3 className="flex items-center gap-2 text-xl font-black text-slate-900"><FaBell className="text-amber-500" /> Alertes qualité</h3>
            <div className="mt-5 space-y-3">
              {warnings.length ? warnings.map((warning, index) => (
                <QualityPill key={index} type="warning"><FaExclamationTriangle className="mr-2 inline" />{warning}</QualityPill>
              )) : <QualityPill type="success"><FaCheckCircle className="mr-2 inline" />Aucune alerte critique détectée.</QualityPill>}
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
            <h3 className="flex items-center gap-2 text-xl font-black text-slate-900"><FaStar className="text-yellow-500" /> Recommandations Forfeo</h3>
            <div className="mt-5 space-y-3">
              {recommendations.length ? recommendations.map((recommendation, index) => (
                <QualityPill key={index} type="info">{recommendation}</QualityPill>
              )) : <QualityPill type="success">Votre configuration actuelle est saine. Continuez à publier des offres et à recueillir des avis.</QualityPill>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <button onClick={() => onNavigate?.('lastcall')} className="rounded-[2rem] border border-red-100 bg-red-50 p-6 text-left transition hover:-translate-y-1 hover:shadow-xl">
          <FaFire className="mb-4 text-3xl text-red-500" />
          <h4 className="text-lg font-black text-slate-900">Remplir les périodes creuses</h4>
          <p className="mt-2 text-sm font-medium text-slate-600">Lancez une offre Last Call pour transformer les invendus en réservations.</p>
        </button>
        <button onClick={() => onNavigate?.('boutique')} className="rounded-[2rem] border border-fuchsia-100 bg-fuchsia-50 p-6 text-left transition hover:-translate-y-1 hover:shadow-xl">
          <FaTicketAlt className="mb-4 text-3xl text-fuchsia-500" />
          <h4 className="text-lg font-black text-slate-900">Créer un forfait cadeau</h4>
          <p className="mt-2 text-sm font-medium text-slate-600">Ajoutez une expérience claire, vendable et prête à offrir.</p>
        </button>
        <button onClick={() => onNavigate?.('profile')} className="rounded-[2rem] border border-blue-100 bg-blue-50 p-6 text-left transition hover:-translate-y-1 hover:shadow-xl">
          <FaStore className="mb-4 text-3xl text-blue-500" />
          <h4 className="text-lg font-black text-slate-900">Améliorer ma fiche</h4>
          <p className="mt-2 text-sm font-medium text-slate-600">Photo, description, coordonnées et liens sociaux augmentent la confiance.</p>
        </button>
      </div>
    </div>
  );
}
